import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, Mic, Clock, MapPin, Sparkles, ArrowUpRight } from "lucide-react";
import { getRecentSearches } from "@/lib/searchHistory";
import { buildQuerySuggestions } from "@/data/searchSuggestions";
import { useSpeechInput } from "@/hooks/useSpeech";

const POPULAR = [
  { name: "Bole", sub: "Popular Area" },
  { name: "CMC", sub: "Residential" },
  { name: "Kazanchis", sub: "Business District" },
  { name: "Sarbet", sub: "Popular Area" },
  { name: "Old Airport", sub: "Premium Area" },
  { name: "Piassa", sub: "Central" },
];
const AI_PROMPTS = [
  "3 bedroom villa in Bole under 20M",
  "Apartments for rent near CMC",
  "Family homes with parking",
  "Land for sale in Ayat",
];

const Row = ({ icon: Icon, title, sub, onClick, testid, trailing }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="tap-press flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 hover:bg-slate-100"
  >
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1E3A8A]/8 text-[#1E3A8A]">
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[15px] font-medium text-slate-800">{title}</span>
      {sub && <span className="block truncate text-xs text-slate-400">{sub}</span>}
    </span>
    {trailing}
  </button>
);

export default function SearchOverlay({ open, initialValue = "", onClose, onSubmit }) {
  const [q, setQ] = useState(initialValue);
  const inputRef = useRef(null);
  const voice = useSpeechInput({ onResult: (t) => setQ(t) });

  const recent = useMemo(() => {
    try { return getRecentSearches().slice(0, 5); } catch { return []; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setQ(initialValue);
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = (text) => {
    const v = (text ?? q ?? "").trim();
    onSubmit?.(v);
  };

  const typing = q.trim().length > 0;
  const suggestions = useMemo(
    () => (typing ? buildQuerySuggestions(q.trim()).flatMap((g) => g.items).slice(0, 6) : []),
    [typing, q]
  );

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[300]" data-testid="search-overlay">
          <motion.div
            className="absolute inset-0 bg-[#0B1A3A]/45 backdrop-blur-md"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={onClose}
            data-testid="search-overlay-backdrop"
          />
          <motion.div
            className="absolute inset-x-0 top-0 mx-auto w-full max-w-2xl px-0 sm:px-4 sm:pt-4"
            initial={{ y: -24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -24, opacity: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <div
              className="flex max-h-[92vh] flex-col rounded-b-3xl bg-white shadow-[0_30px_80px_rgba(8,15,40,0.4)] sm:rounded-3xl"
              style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
              {/* Input */}
              <div className="flex items-center gap-2 border-b border-border p-3">
                <div className="flex flex-1 items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 focus-within:border-[#1E3A8A] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(30,58,138,0.12)]">
                  <Search className="h-5 w-5 shrink-0 text-[#1E3A8A]" />
                  <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                    placeholder="Search by city, neighborhood, address or landmark…"
                    data-testid="search-overlay-input"
                    className="h-12 w-full border-0 bg-transparent text-[15px] text-slate-900 outline-none placeholder:text-slate-400"
                  />
                  {q && (
                    <button type="button" aria-label="Clear" data-testid="search-overlay-clear" onClick={() => { setQ(""); inputRef.current?.focus(); }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-600">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  <button type="button" aria-label="Voice search" onClick={() => (voice.supported ? (voice.listening ? voice.stop() : voice.start()) : null)}
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${voice.listening ? "animate-pulse bg-[#10B981]/15 text-[#10B981]" : "text-slate-500 hover:bg-slate-100 hover:text-[#1E3A8A]"}`}>
                    <Mic className="h-5 w-5" />
                  </button>
                </div>
                <button type="button" onClick={onClose} data-testid="search-overlay-close" aria-label="Close search"
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="thin-scroll flex-1 overflow-y-auto p-2 sm:p-3">
                {typing ? (
                  <div className="space-y-0.5">
                    {suggestions.map((s, i) => (
                      <Row key={`${s.value}-${i}`} icon={s.type === "area" ? MapPin : Search} title={s.label} sub={s.sub}
                        testid={`search-overlay-suggestion-${i}`} onClick={() => submit(s.value)} trailing={<ArrowUpRight className="h-4 w-4 text-slate-300" />} />
                    ))}
                    <Row icon={Sparkles} title={`Ask Abay AI: “${q.trim()}”`} sub="AI-powered search" testid="search-overlay-ai" onClick={() => submit(q)} />
                  </div>
                ) : (
                  <div className="space-y-4 py-1">
                    {recent.length > 0 && (
                      <div>
                        <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recent Searches</p>
                        {recent.map((r, i) => (
                          <Row key={`recent-${i}`} icon={Clock} title={r} testid={`search-overlay-recent-${i}`} onClick={() => submit(r)} />
                        ))}
                      </div>
                    )}
                    <div>
                      <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Popular Areas</p>
                      {POPULAR.map((p, i) => (
                        <Row key={`pop-${i}`} icon={MapPin} title={p.name} sub={p.sub} testid={`search-overlay-popular-${i}`} onClick={() => submit(p.name)} />
                      ))}
                    </div>
                    <div>
                      <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">AI Suggestions</p>
                      {AI_PROMPTS.map((p, i) => (
                        <Row key={`ai-${i}`} icon={Sparkles} title={p} testid={`search-overlay-aiprompt-${i}`} onClick={() => submit(p)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
